import { useState, useCallback, useMemo } from "react";
import {
  FullscreenTakeover,
  Card,
  Text,
  Button,
  Flex,
  type FileObjectType,
  LinearProgressIndicator,
  toast,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { DataStudioJobTypeEnum } from "~/graphql/supergraphEnums";
import useDataStudioUploadFiles from "~/hooks/useDataStudioUploadFiles/useDataStudioUploadFiles";
import {
  DEFAULT_CONFIG_PATHS,
  ArenaLockAction,
  ConfigurationType,
  UploadState,
} from "../constants";
import { useCreateOnboardingJob } from "../hooks/useArenaLockUnlock";
import { validateFiles } from "../utils";
import { ActionSelector } from "./ActionSelector";
import { ConfigurationTypeSelector } from "./ConfigurationTypeSelector";
import { DefaultConfigurationTable } from "./DefaultConfigurationTable";
import { EmailInput } from "./EmailInput";
import { FileUploadSection } from "./FileUploadSection";
import * as styles from "./styles_vanilla.css";
import type { ArenaLockUnlockUploadModalProps } from "../types";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ProgressSectionProps = {
  uploadState: UploadState;
  configurationType: ConfigurationType;
};

const ProgressSection = ({ uploadState, configurationType }: ProgressSectionProps) => {
  if (uploadState === UploadState.UPLOADING) {
    return (
      <div className={styles.progressSection}>
        <Flex alignItems="center" gap={Sizes.XS} className={styles.progressHeader}>
          <Icon type="upload" size="small" color={Colors.Primary600} />
          <Text variant="strong-sm">
            {configurationType === ConfigurationType.CUSTOM
              ? "Uploading file to S3..."
              : "Processing..."}
          </Text>
        </Flex>
        <LinearProgressIndicator progressPercentage={50} />
      </div>
    );
  }

  if (uploadState === UploadState.TRIGGERING) {
    return (
      <div className={styles.progressSection}>
        <Flex alignItems="center" gap={Sizes.XS} className={styles.progressHeader}>
          <Icon type="cog" size="small" color={Colors.Primary600} />
          <Text variant="strong-sm">
            {configurationType === ConfigurationType.CUSTOM
              ? "File uploaded. Starting arena operation..."
              : "Starting arena operation..."}
          </Text>
        </Flex>
        <LinearProgressIndicator progressPercentage={75} />
      </div>
    );
  }

  return null;
};

type ErrorSectionProps = {
  errorMessage: string;
};

const ErrorSection = ({ errorMessage }: ErrorSectionProps) => {
  if (!errorMessage) return null;

  return (
    <div className={styles.errorSection}>
      <Flex alignItems="center" gap={Sizes.XS}>
        <Icon type="exclamation-triangle" size="small" color={Colors.Danger600} />
        <Text variant="strong-sm" className={styles.errorText}>
          {errorMessage}
        </Text>
      </Flex>
    </div>
  );
};

type SuccessSectionProps = {
  uploadState: UploadState;
  successMessage: string;
  email: string;
  jobId: string;
};

const SuccessSection = ({ uploadState, successMessage, email, jobId }: SuccessSectionProps) => {
  if (uploadState !== UploadState.SUCCESS || !successMessage) return null;

  return (
    <div className={styles.successSection}>
      <Flex alignItems="center" gap={Sizes.XS} className={styles.successHeader}>
        <Icon type="check-circle" size="small" color={Colors.Success600} />
        <Text variant="strong-sm" className={styles.successText}>
          {successMessage}
        </Text>
      </Flex>
      <Text variant="secondary-sm" className={styles.successDetails}>
        Operation executed by: {email}
      </Text>
      {jobId && (
        <Text variant="secondary-sm" className={styles.successJobId}>
          Job ID: {jobId}
        </Text>
      )}
    </div>
  );
};

export const ArenaLockUnlockConfiguration = ({
  isOpen,
  onClose,
  onSuccess,
  arenaDomain,
  environment,
  action,
  arenaName,
  userEmail,
}: ArenaLockUnlockUploadModalProps) => {
  const [uploadState, setUploadState] = useState<UploadState>(UploadState.IDLE);
  const [selectedFiles, setSelectedFiles] = useState<FileObjectType[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [jobId, setJobId] = useState<string>("");

  // Configuration type state
  const [configurationType, setConfigurationType] = useState<ConfigurationType>(
    ConfigurationType.DEFAULT,
  );

  // Action state (allow user to change the action)
  const [selectedAction, setSelectedAction] = useState<ArenaLockAction>(action);

  // Email input state
  const [email, setEmail] = useState<string>(userEmail);
  const [emailError, setEmailError] = useState<string>("");

  // Hooks
  const { uploadFilesToS3, loading: uploadLoading } = useDataStudioUploadFiles();
  const {
    createJob,
    loading: jobCreationLoading,
    error: jobCreationError,
  } = useCreateOnboardingJob();

  // Memoize trimmed email to avoid repeated trim() calls
  const trimmedEmail = useMemo(() => email.trim(), [email]);

  // Handle file selection
  const handleFileSelected = useCallback((files: FileObjectType[]) => {
    const validation = validateFiles(files);

    if (!validation.isValid) {
      setErrorMessage(validation.errorMessage || "Invalid file");
      return;
    }

    setSelectedFiles(files);
    setUploadState(UploadState.IDLE);
    setErrorMessage("");
    setSuccessMessage("");
  }, []);

  // Handle triggering arena lock/unlock operation (combines upload + job creation like Arena Creation)
  const handleTriggerOperation = useCallback(async () => {
    // Validate email input first
    if (!trimmedEmail) {
      setErrorMessage(
        "Please enter a valid email address before triggering the arena operation",
      );
      setEmailError("Email address is required");
      return;
    }

    // Email format validation
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setErrorMessage("Please enter a valid email address format (e.g., user@domain.com)");
      setEmailError("Please enter a valid email address format");
      return;
    }

    // Clear email error if validation passes
    setEmailError("");

    // Normalize values to lowercase for backend API (like Arena Creation)
    const normalizedArenaDomain = arenaDomain.toLowerCase();
    const normalizedArenaName = (arenaName || arenaDomain).toLowerCase();
    const normalizedEmail = trimmedEmail.toLowerCase();

    // Determine the S3 key to use
    let s3KeyToUse: string;

    if (configurationType === ConfigurationType.DEFAULT) {
      // Use the predefined path for default configuration
      s3KeyToUse = DEFAULT_CONFIG_PATHS[selectedAction];
    } else {
      // For custom configuration, validate file selection
      const fileValidation = validateFiles(selectedFiles);
      if (!fileValidation.isValid) {
        setErrorMessage(fileValidation.errorMessage || "Please select a TSV file to upload");
        return;
      }

      // Start the combined upload + job creation process
      setUploadState(UploadState.UPLOADING);
      setErrorMessage("");

      try {
        // Step 1: Upload file to S3 using Data Studio mutation
        const uploadResult = await uploadFilesToS3({
          files: selectedFiles,
          arenaDomain: normalizedArenaDomain,
          arenaUserEmail: normalizedEmail,
          jobType: DataStudioJobTypeEnum.ArenaLock,
          environment,
        });

        if (!uploadResult || uploadResult.length === 0) {
          throw new Error("Upload failed - no files uploaded");
        }

        const uploadedFile = uploadResult[0];
        // Use relative path (arenaDomain/filename) - backend scripts expect this format
        const relativeS3Path = `${normalizedArenaDomain}/${uploadedFile.file.name}`;
        s3KeyToUse = relativeS3Path;

        // Update state to show upload completed, now triggering job
        setUploadState(UploadState.TRIGGERING);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : "Failed to upload file to S3";
        setErrorMessage(errorMsg);
        setUploadState(UploadState.ERROR);
        toast.error(errorMsg);
        return;
      }
    }

    // Step 2: Create the onboarding job (for both default and custom configurations)
    if (configurationType === ConfigurationType.DEFAULT) {
      setUploadState(UploadState.TRIGGERING);
      setErrorMessage("");
    }

    try {
      const result = await createJob(
        normalizedArenaDomain,
        normalizedEmail,
        selectedAction,
        s3KeyToUse,
        normalizedArenaName,
        environment,
      );

      if (result) {
        const jobIdValue = result.id || "";
        setJobId(jobIdValue);
        setSuccessMessage(
          result.message || `Arena ${selectedAction} operation started successfully`,
        );
        setUploadState(UploadState.SUCCESS);
        toast.success(`Arena ${selectedAction} operation started successfully`);

        // Call the onSuccess callback with job ID
        onSuccess(jobIdValue, result.s3Uri || "");
      } else {
        throw new Error("Failed to trigger arena operation");
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : jobCreationError || `Failed to trigger arena ${selectedAction} operation`;
      setErrorMessage(errorMsg);
      setUploadState(UploadState.ERROR);
      toast.error(errorMsg);
    }
  }, [
    configurationType,
    selectedFiles,
    uploadFilesToS3,
    arenaDomain,
    environment,
    selectedAction,
    trimmedEmail,
    arenaName,
    createJob,
    jobCreationError,
    onSuccess,
  ]);

  // Handle modal close
  const handleClose = useCallback(() => {
    setUploadState(UploadState.IDLE);
    setSelectedFiles([]);
    setErrorMessage("");
    setSuccessMessage("");
    setJobId("");
    setConfigurationType(ConfigurationType.DEFAULT);
    setEmail("");
    setEmailError("");
    onClose();
  }, [onClose]);

  const isLoading = useMemo(
    () =>
      uploadLoading ||
      jobCreationLoading ||
      uploadState === UploadState.UPLOADING ||
      uploadState === UploadState.TRIGGERING,
    [uploadLoading, jobCreationLoading, uploadState],
  );

  const canTrigger = useMemo(() => {
    const hasValidEmail = trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
    if (!hasValidEmail) return false;

    // For default configuration, can trigger immediately
    if (configurationType === ConfigurationType.DEFAULT) {
      return uploadState === UploadState.IDLE;
    }

    // For custom configuration, must have file selected
    return selectedFiles.length > 0 && uploadState === UploadState.IDLE;
  }, [configurationType, uploadState, trimmedEmail, selectedFiles.length]);

  const isComplete = useMemo(() => uploadState === UploadState.SUCCESS, [uploadState]);

  if (!isOpen) return null;

  return (
    <FullscreenTakeover>
      {/* Custom Header with Back Button */}
      <div className={styles.customHeader}>
        <Button
          variant="neutral"
          onClick={handleClose}
          data-testid="back-button"
          icon="chevron-left"
          iconPosition="left"
        >
          Back
        </Button>
        <Text variant="h1">Arena Lock/Unlock Configuration File</Text>
        <div></div> {/* Empty div for flex spacing */}
      </div>
      <FullscreenTakeover.Body>
        <div className={styles.modalContent}>
          <Card className="inset-lg">
            <Flex alignItems="center" gap={Sizes.MD} className={styles.modalHeader}>
              <Icon type="lock" size="large" color={Colors.Primary600} />
              <div>
                <Text variant="h3" className={styles.modalTitle}>
                  Arena Lock/Unlock Configuration
                </Text>
                <Text variant="secondary-md">
                  {arenaName || arenaDomain} ({environment})
                </Text>
              </div>
            </Flex>

            <ActionSelector
              selectedAction={selectedAction}
              isLoading={isLoading}
              isComplete={isComplete}
              onActionChange={setSelectedAction}
            />

            <EmailInput
              selectedAction={selectedAction}
              email={email}
              emailError={emailError}
              onEmailChange={setEmail}
              onEmailErrorChange={setEmailError}
            />

            <ConfigurationTypeSelector
              selectedAction={selectedAction}
              configurationType={configurationType}
              isLoading={isLoading}
              isComplete={isComplete}
              onConfigurationTypeChange={setConfigurationType}
            />

            {configurationType === ConfigurationType.DEFAULT && (
              <DefaultConfigurationTable selectedAction={selectedAction} />
            )}

            {configurationType === ConfigurationType.CUSTOM && (
              <FileUploadSection
                selectedAction={selectedAction}
                selectedFiles={selectedFiles}
                isLoading={isLoading}
                isComplete={isComplete}
                onFileSelected={handleFileSelected}
              />
            )}

            <ProgressSection uploadState={uploadState} configurationType={configurationType} />
            <ErrorSection errorMessage={errorMessage} />
            <SuccessSection
              uploadState={uploadState}
              successMessage={successMessage}
              email={trimmedEmail}
              jobId={jobId}
            />

            <Flex gap={Sizes.SM} justifyContent="flex-end" className={styles.actionButtons}>
              <Button variant="neutral" onClick={handleClose} disabled={isLoading}>
                {isComplete ? "Close" : "Cancel"}
              </Button>

              {/* Single Trigger Operation Button - handles both upload (if needed) and job creation */}
              {uploadState === UploadState.IDLE && (
                <Button
                  variant="primary"
                  onClick={handleTriggerOperation}
                  disabled={!canTrigger || isLoading}
                  isLoading={isLoading}
                >
                  {selectedAction === ArenaLockAction.LOCK ? "Lock Arena" : "Unlock Arena"}
                </Button>
              )}
            </Flex>
          </Card>
        </div>
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};

// Export with old name for backward compatibility
export const ArenaLockUnlockUploadModal = ArenaLockUnlockConfiguration;
